import { useMemo, useState } from "react";
import {
  selectAllRecipes,
  selectRecipesByCollection,
  setCollectionRecipes,
  compareRecipeTitles,
  recipeImageUri,
  useT,
  useAppDispatch,
  useAppSelector,
  type RootState,
} from "@reciperoll/core";
import { Check } from "react-feather";
import { StorageImage } from "./StorageImage";
import { useToast } from "./Toast";

// Pick which recipes belong to a collection (checkbox list, pre-checked from the
// current membership). Saving replaces the set via setCollectionRecipes — so
// unchecking a recipe removes it, checking adds it.
export function AddRecipesModal({
  collectionId,
  onClose,
}: {
  collectionId: string;
  onClose: () => void;
}) {
  const t = useT();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const recipes = useAppSelector(selectAllRecipes);
  const inCollection = useAppSelector((s: RootState) =>
    selectRecipesByCollection(s, collectionId),
  );

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(inCollection.map((r) => r.id)),
  );
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const base = query
      ? recipes.filter((r) => r.title.toLowerCase().includes(query))
      : recipes;
    // Alphabetical, ignoring leading quotes/punctuation.
    return [...base].sort((a, b) => compareRecipeTitles(a.title, b.title));
  }, [recipes, q]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = () => {
    dispatch(setCollectionRecipes({ collectionId, recipeIds: [...selected] }));
    toast.show(
      selected.size === 0
        ? t("collections.emptied")
        : t("collections.recipesInCollection", { count: selected.size }),
    );
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[80vh] flex flex-col rounded-2xl bg-surface p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-ink mb-3">{t("collections.addRecipes")}</h2>
        <input
          className="w-full rounded-lg border border-line px-3 py-2 text-sm outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("library.filterByName")}
          autoFocus
        />

        <div className="flex-1 overflow-y-auto mt-3 -mx-1 pr-1">
          {filtered.length === 0 ? (
            <p className="text-sm text-ink-faint px-1 py-4">{t("collections.noRecipes")}</p>
          ) : (
            filtered.map((r) => {
              const checked = selected.has(r.id);
              return (
                <button
                  key={r.id}
                  onClick={() => toggle(r.id)}
                  className="w-full flex items-center gap-3 px-1 py-2 text-left rounded-lg hover:bg-surface-2 cursor-pointer"
                >
                  <div
                    className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                      checked ? "bg-terracotta border-terracotta" : "border-line"
                    }`}
                  >
                    {checked && (
                      <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                    )}
                  </div>
                  <StorageImage
                    stored={recipeImageUri(r)}
                    imgClassName="w-10 h-10 rounded-lg object-cover shrink-0 bg-surface-2"
                    fallback={<div className="w-10 h-10 rounded-lg bg-surface-2 shrink-0" />}
                  />
                  <span className="text-sm font-medium text-ink truncate">{r.title}</span>
                </button>
              );
            })
          )}
        </div>

        <div className="mt-4 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border border-line py-2.5 font-semibold text-ink-soft hover:bg-surface-2 cursor-pointer"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={save}
            className="flex-1 rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors py-2.5 font-semibold text-white cursor-pointer"
          >
            {t("collections.saveCount", { count: selected.size })}
          </button>
        </div>
      </div>
    </div>
  );
}
